/**
 * useProvidersList Hook
 *
 * Fetches and manages provider list data for the audit dashboard
 */

import { useMemo } from 'react';

import { useGetDashboardProvidersQuery } from '../store/api';
import type { ProviderSummary } from '../types';

interface UseProvidersListResult {
  /** List of providers */
  providers: ProviderSummary[];
  /** Total number of providers */
  total: number;
  /** Whether data is loading */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Refetch function */
  refetch: () => void;
  /** Whether list is empty */
  isEmpty: boolean;
}

export const useProvidersList = (): UseProvidersListResult => {
  const { data, isLoading, error, refetch } = useGetDashboardProvidersQuery();

  const providers = useMemo(() => data?.providers || [], [data]);
  const total = data?.total || 0;
  const isEmpty = !isLoading && providers.length === 0;

  return {
    providers,
    total,
    isLoading,
    error: error ? 'Failed to load providers list' : null,
    refetch,
    isEmpty,
  };
};

export default useProvidersList;

